import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useI18n } from "../i18n";
import {
  ManagementDialog,
  ManagementPanel,
  ManagementPagination,
} from "../managementUI";
import { date, duration, fresh, number, type MonitoringData } from "./model";
import { Empty, Note, Table } from "./ui";
import { LLMUsage } from "./LLMUsage";

export function TeamUsage({
  data,
  now,
}: {
  data: MonitoringData;
  now: number;
}) {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const search = params.get("team_search") || "";
  const sort = params.get("team_sort") || "cpu";
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const placement = fresh(data.placement, now) ? data.placement.data : null;
  const sandbox = fresh(data.sandbox, now) ? data.sandbox.data : null;
  const inventory = new Set(
    [
      ...(placement?.placements ?? []).map((p) => p.placement.team_id),
      ...(sandbox?.fleet.teams ?? []).map((team) => team.team_id),
      ...(sandbox?.runtimes ?? []).map((r) => r.team_id),
    ].filter(Boolean),
  );
  const items = [...inventory]
    .filter((id) => id.toLowerCase().includes(search.toLowerCase()))
    .map((id) => ({
      id,
      placement: placement?.placements.find((p) => p.placement.team_id === id),
      allocation: sandbox?.fleet.teams.find((team) => team.team_id === id),
      runtimes: sandbox?.runtimes.filter((r) => r.team_id === id) ?? [],
    }))
    .sort((a, b) =>
      sort === "team"
        ? a.id.localeCompare(b.id)
        : sort === "containers"
          ? (b.allocation?.pod_count ?? 0) - (a.allocation?.pod_count ?? 0)
          : (b.allocation?.allocated_cpu_milli ?? 0) -
            (a.allocation?.allocated_cpu_milli ?? 0),
    );
  const current = items.find((item) => item.id === selected);
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
    setOffset(0);
  };
  return (
    <>
      <ManagementPanel title={t("团队资源用量", "Team resource use")}>
        <Note>
          {t(
            "此表展示 Placement 与 Sandbox 已登记团队的实时资源分配。下方单独展示 Token 用量；数据库实际占用、网络消耗和周期计费配额尚未接入。",
            "This table shows live allocations for Teams registered in Placement and Sandbox. Token usage is shown separately below; database size, network consumption and billing quotas are not connected.",
          )}
        </Note>
        {!placement || !sandbox ? (
          <Note>
            {t(
              "部分资源来源不可用，团队列表和用量覆盖可能不完整。",
              "Some resource sources are unavailable; Team inventory and usage coverage may be incomplete.",
            )}
          </Note>
        ) : null}
        <div className="monitor-toolbar">
          <label>
            {t("搜索 Team", "Search Team")}
            <input
              value={search}
              onChange={(e) => update("team_search", e.target.value)}
            />
          </label>
          <label>
            {t("排序", "Sort")}
            <select
              value={sort}
              onChange={(e) => update("team_sort", e.target.value)}
            >
              <option value="cpu">
                {t("分配 CPU 从高到低", "Allocated CPU descending")}
              </option>
              <option value="containers">
                {t("容器数量从高到低", "Container count descending")}
              </option>
              <option value="team">Team ID</option>
            </select>
          </label>
          <span>{t("时间口径：当前快照", "Time basis: current snapshot")}</span>
        </div>
        {!items.length ? (
          <Empty>
            {placement || sandbox
              ? t("没有匹配的已登记团队。", "No matching registered Teams.")
              : t("团队资源来源不可用。", "Team resource sources unavailable.")}
          </Empty>
        ) : (
          <>
            <Table
              headers={[
                "Team",
                t("容器", "Containers"),
                t("分配 CPU", "CPU allocated"),
                t("分配内存", "Memory allocated"),
                t("主库绑定", "Main DB binding"),
                t("操作", "Actions"),
              ]}
            >
              {items.slice(offset, offset + 20).map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.id}</strong>
                    <span>
                      {item.placement?.placement.owner_instance_id || "—"}
                    </span>
                  </td>
                  <td>
                    {sandbox ? number(item.allocation?.pod_count ?? 0) : "—"}
                  </td>
                  <td>
                    {sandbox
                      ? `${number((item.allocation?.allocated_cpu_milli ?? 0) / 1000)} vCPU`
                      : "—"}
                  </td>
                  <td>
                    {sandbox
                      ? `${number((item.allocation?.allocated_mem_mib ?? 0) / 1024)} GiB`
                      : "—"}
                  </td>
                  <td>
                    {item.placement?.placement.database_binding_ref
                      ? t("已绑定", "Bound")
                      : t("未知", "Unknown")}
                  </td>
                  <td>
                    <button
                      className="secondary-button"
                      onClick={() => setSelected(item.id)}
                    >
                      {t("用量详情", "Usage details")}
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
            <ManagementPagination
              total={items.length}
              offset={offset}
              limit={20}
              onChange={setOffset}
            />
          </>
        )}
      </ManagementPanel>
      <LLMUsage />
      <ManagementPanel title={t("计量覆盖", "Metering coverage")}>
        <Table
          headers={[
            t("资源类型", "Resource"),
            t("现有来源", "Current evidence"),
            t("后续计量", "Planned metering"),
          ]}
        >
          <tr>
            <td>Token</td>
            <td>
              {t(
                "LiteLLM返回的请求用量记录",
                "Request usage records returned by LiteLLM",
              )}
            </td>
            <td>
              {t(
                "输入、输出、缓存 Token；按模型及执行归属",
                "Input, output, cache tokens; by model and execution",
              )}
            </td>
          </tr>
          <tr>
            <td>{t("运行环境", "Environments")}</td>
            <td>
              {t(
                "实时容器与分配规格",
                "Live containers and allocated specifications",
              )}
            </td>
            <td>
              {t(
                "运行时长、资源时长、实际利用率",
                "Runtime duration, resource-hours, utilization",
              )}
            </td>
          </tr>
          <tr>
            <td>{t("数据库", "Databases")}</td>
            <td>
              {t("仅 Team 主库绑定事实", "Team main database bindings only")}
            </td>
            <td>
              {t(
                "主库、开发库、发布库的容量和可归属消耗",
                "Main, development, published-app DB capacity and attributable use",
              )}
            </td>
          </tr>
          <tr>
            <td>{t("存储与网络", "Storage and network")}</td>
            <td>{t("未接入计量", "Metering not connected")}</td>
            <td>
              {t(
                "存储持有量、流量及闲置资源",
                "Stored volume, traffic, and idle resources",
              )}
            </td>
          </tr>
        </Table>
      </ManagementPanel>
      {current ? (
        <ManagementDialog
          title={`${t("Team 资源", "Team resources")} · ${current.id}`}
          onClose={() => setSelected(null)}
        >
          <Note>
            {t(
              "以下为当前运行资源，不是账单。计费周期、套餐额度及剩余额度尚未接入。",
              "Current runtime resources, not a bill. Billing periods, plan quotas, and balances are not connected.",
            )}
          </Note>
          <dl className="monitor-detail">
            <dt>{t("主库绑定", "Main DB binding")}</dt>
            <dd>{current.placement?.placement.database_binding_ref || "—"}</dd>
            <dt>{t("Schema版本", "Schema version")}</dt>
            <dd>{current.placement?.placement.schema_version || "—"}</dd>
            <dt>{t("当前资源分配时点", "Allocation observed")}</dt>
            <dd>{date(sandbox?.observed_at)}</dd>
          </dl>
          <h3>{t("当前 Sandbox", "Current Sandboxes")}</h3>
          {!current.runtimes.length ? (
            <Empty>
              {sandbox
                ? t("当前没有活跃 Sandbox。", "No active Sandboxes.")
                : t("数据不可用。", "Data unavailable.")}
            </Empty>
          ) : (
            <ul className="monitor-list">
              {current.runtimes.map((runtime) => (
                <li key={runtime.sandbox_id}>
                  <strong>{runtime.sandbox_id}</strong>
                  <span>
                    {runtime.resource_class} · {runtime.scope}
                  </span>
                  <span>
                    {t("空闲", "Idle")} {duration(runtime.idle_seconds)} ·{" "}
                    {runtime.pinned
                      ? t("已保留", "Retained")
                      : t("按生命周期策略回收", "Lifecycle-managed")}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <button
            className="secondary-button"
            onClick={() => setSelected(null)}
          >
            {t("关闭", "Close")}
          </button>
        </ManagementDialog>
      ) : null}
    </>
  );
}
